import React, { useState, useEffect, useCallback } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import FMBLogo from "../../../assests/FMBlogo.png"; // Import the image
import ReminderImage from "../../../assests/landscape/0.png"; // Import the reminder image
import { getActiveAds, getQueues } from "../../../api/api";
import useQueueUpdates from "../../../hooks/useQueueUpdates";

const DisplayAd = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentQueueNumber, setCurrentQueueNumber] = useState("00");
    const [searchParams] = useSearchParams();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

    // Get branch_id from URL params
    const branchId = searchParams.get("branch_id");

    useEffect(() => {
        fetchActiveAds();
        fetchCurrentQueue();
    }, [branchId]);

    // Auto-refresh ads every 60 seconds
    useEffect(() => {
        const refreshInterval = setInterval(() => {
            console.log("Auto-refreshing display ads...");
            fetchActiveAds();
        }, 60000); // 60 seconds

        return () => clearInterval(refreshInterval);
    }, [branchId]);

    // Fetch latest queue number for the branch.
    // The display must always show the LATEST Assist-clicked queue number, even
    // when multiple queues are SERVING simultaneously or when the latest
    // assist-clicked queue has since been marked Done. The backend already returns
    // exactly that in `last_queue_number` — it orders SERVING+ASSISTED queues by
    // QueueSvcTime desc (the actual assist-click time, which is no longer
    // overwritten on Done/auto-expire), so we just trust it directly. Done, new
    // waiting queues, auto-expire, and Lost do NOT change this value.
    const fetchCurrentQueue = useCallback(async () => {
        if (!branchId) return;

        try {
            const data = await getQueues(branchId);
            const lastQueueNumber = Array.isArray(data) ? null : data?.last_queue_number;

            if (lastQueueNumber != null) {
                setCurrentQueueNumber(String(lastQueueNumber).padStart(2, "0"));
            }
        } catch (err) {
            console.error("Error fetching current queue:", err);
        }
    }, [branchId]);

    // Real-time queue updates. Polling only kicks in while the socket is down.
    //
    // Update the display when:
    // - Staff presses Assist -> queue moves to SERVING
    // - Staff uses Direct Assist -> queue is created directly as ASSISTED
    // Done (ASSISTED from SERVING) is intentionally ignored so the display does
    // not auto-advance to the next waiting number.
    useQueueUpdates({
        branchId,
        onRefresh: fetchCurrentQueue,
        onQueueEvent: (queueData, action) => {
            const isServing =
                queueData.QueueStatus === "SERVING" ||
                queueData.status === "SERVING" ||
                queueData.QueueStatus === "serving" ||
                queueData.status === "serving";
            const isDirectAssist = action === "direct_assist";

            if (isServing || isDirectAssist) {
                setCurrentQueueNumber(
                    queueData.QueueNr || queueData.number || "00"
                );
            }
        },
    });

    const fetchActiveAds = async () => {
        try {
            setLoading(true);
            const response = await getActiveAds("landscape", null, branchId);
            if (response.success) {
                setAds(response.data || []);
            } else {
                setError("Failed to load ads");
            }
        } catch (err) {
            setError("Error loading ads");
            console.error("Error fetching ads:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (ads.length > 0) {
            const interval = setInterval(() => {
                setCurrentIndex((prevIndex) => {
                    // Calculate total items: ads + reminder images
                    // Every 4 ads, we insert 1 reminder image
                    const totalItems = ads.length + Math.floor(ads.length / 4);
                    return (prevIndex + 1) % totalItems;
                });
            }, 5000); // Change image every 5 seconds

            return () => clearInterval(interval);
        }
    }, [ads]);

    // Function to get the current image and alt text
    const getCurrentImage = () => {
        if (ads.length === 0) return { src: null, alt: "" };

        // Check if current index should show reminder image
        // Show reminder image after every 4 ads (at positions 4, 9, 14, etc.)
        if ((currentIndex + 1) % 5 === 0) {
            return {
                src: ReminderImage,
                alt: "Please collect your queue number",
            };
        }

        // Calculate which ad to show, accounting for inserted reminder images
        const adIndex = currentIndex - Math.floor(currentIndex / 5);
        return {
            src: ads[adIndex].image_url,
            alt: ads[adIndex].title,
        };
    };

    // Show loading or error state if needed
    if (loading) {
        return (
            <Box sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                height: "100vh",
                width: "100%"
            }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        height: isMobile ? "50vh" : "100vh",
                        width: isMobile ? "100%" : "25%",
                    }}
                >
                    <img
                        src={FMBLogo}
                        alt="FMB Logo"
                        style={{
                            width: "28%",
                            transition: "opacity 1s ease-in-out",
                            marginBottom: "50px",
                        }}
                    />
                    <div
                        style={{
                            fontSize: isMobile ? "120px" : "min(140px, 9vw)",
                            marginTop: "-80px",
                            width: "100%",
                            textAlign: "center",
                        }}
                    >
                        <h1 style={{ margin: 0 }}>{currentQueueNumber}</h1>
                    </div>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: isMobile ? "100%" : "75%",
                        height: isMobile ? "50vh" : "100vh",
                        fontSize: isMobile ? "18px" : "24px",
                        color: "#666",
                    }}
                >
                    Loading ads...
                </Box>
            </Box>
        );
    }

    if (error || ads.length === 0) {
        return (
            <Box sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                height: "100vh",
                width: "100%"
            }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        height: isMobile ? "50vh" : "100vh",
                        width: isMobile ? "100%" : "25%",
                    }}
                >
                    <img
                        src={FMBLogo}
                        alt="FMB Logo"
                        style={{
                            width: "28%",
                            transition: "opacity 1s ease-in-out",
                            marginBottom: "50px",
                        }}
                    />
                    <div
                        style={{
                            fontSize: isMobile ? "120px" : "min(140px, 9vw)",
                            marginTop: "-80px",
                            width: "100%",
                            textAlign: "center",
                        }}
                    >
                        <h1 style={{ margin: 0 }}>{currentQueueNumber}</h1>
                    </div>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: isMobile ? "100%" : "75%",
                        height: isMobile ? "50vh" : "100vh",
                        fontSize: isMobile ? "18px" : "24px",
                        color: "#666",
                    }}
                >
                    {error || "No active landscape ads available"}
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            height: "100vh",
            width: "100%"
        }}>
            {/* Left side - Queue number (25% width) */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    height: isMobile ? "50vh" : "100vh",
                    width: isMobile ? "100%" : "25%",
                }}
            >
                <img
                    src={FMBLogo}
                    alt="FMB Logo"
                    style={{
                        width: "28%",
                        transition: "opacity 1s ease-in-out",
                        marginBottom: "50px",
                    }}
                />
                <div
                    style={{
                        fontSize: isMobile ? "120px" : "min(140px, 9vw)",
                        marginTop: "-80px",
                        width: "100%",
                        textAlign: "center",
                    }}
                >
                    <h1 style={{ margin: 0 }}>{currentQueueNumber}</h1>
                </div>
            </Box>

            {/* Right side - Landscape ads (75% width) */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: isMobile ? "100%" : "75%",
                    height: isMobile ? "50vh" : "100vh",
                    padding: 1,
                    boxSizing: "border-box",
                }}
            >
                <img
                    src={getCurrentImage().src}
                    alt={getCurrentImage().alt}
                    style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                        objectPosition: "center center",
                        transition: "opacity 1s ease-in-out",
                        display: "block",
                    }}
                    onLoad={(e) => {
                        // Ensure image scales properly on load
                        const img = e.target;
                        const container = img.parentElement;
                        if (container) {
                            const containerWidth = container.clientWidth;
                            const containerHeight = container.clientHeight;
                            const imgAspectRatio = img.naturalWidth / img.naturalHeight;
                            const containerAspectRatio = containerWidth / containerHeight;

                            // Adjust sizing based on aspect ratios
                            if (imgAspectRatio > containerAspectRatio) {
                                img.style.width = "100%";
                                img.style.height = "auto";
                            } else {
                                img.style.width = "auto";
                                img.style.height = "100%";
                            }
                        }
                    }}
                />
            </Box>
        </Box >
    );
};

export default DisplayAd;
